import { useCallback } from 'react';
import { cn, Dialog, DialogContent } from '@sib/ui';
import {
  ConfirmDeleteView,
  ConfirmResendView,
  ShareDialogView,
} from './ShareDialogViews';
import { useRemoveAccessDialog } from './use-remove-access-dialog';
import { useResendInvitationDialog } from './use-resend-invitation-dialog';
import { DialogView, useFormProps, useInviteesList } from './ShareDialog.utils';

interface ShareDialogProps {
  onClose: () => void;
  open?: boolean;
}

export const ShareDialog = ({ onClose, open }: ShareDialogProps) => {
  const {
    isLoading: isListLoading,
    invitees,
    onInviteesAdd,
    onInviteeRemove,
  } = useInviteesList();

  const formProps = useFormProps({ invitees });

  const resendInvitationDialog = useResendInvitationDialog();
  const removeAccessDialog = useRemoveAccessDialog({
    onRemove: onInviteeRemove,
  });

  const currentView: DialogView = removeAccessDialog.open
    ? DialogView.ConfirmRemove
    : resendInvitationDialog.open
      ? DialogView.ConfirmResend
      : DialogView.Main;

  const onModalClose = useCallback(() => {
    onClose();
    formProps.reset();
  }, [onClose, formProps]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) return;
      if (currentView === DialogView.ConfirmRemove)
        return removeAccessDialog.onClose();
      if (currentView === DialogView.ConfirmResend)
        return resendInvitationDialog.onClose();

      onModalClose();
    },
    [currentView, removeAccessDialog, resendInvitationDialog, onModalClose]
  );

  const isConfirmationView =
    currentView === DialogView.ConfirmRemove ||
    currentView === DialogView.ConfirmResend;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          isConfirmationView
            ? 'flex max-w-[95vw] flex-col items-center justify-center gap-7 rounded-lg bg-white p-7 text-center sm:max-w-[727px] sm:min-w-[520px]'
            : 'max-h-[95vh] max-w-[98vw] overflow-y-auto px-2 py-7 sm:max-w-[70vw] sm:px-[50px] md:min-w-[1000px]'
        )}
      >
        {currentView === DialogView.ConfirmRemove && (
          <ConfirmDeleteView removeAccessDialog={removeAccessDialog} />
        )}
        {currentView === DialogView.ConfirmResend && (
          <ConfirmResendView resendInvitationDialog={resendInvitationDialog} />
        )}
        {currentView === DialogView.Main && (
          <ShareDialogView
            formProps={formProps}
            onInviteesAdd={onInviteesAdd}
            onModalClose={onModalClose}
            isListLoading={isListLoading}
            invitees={invitees}
            openResendInvitationDialog={resendInvitationDialog.onOpenDialog}
            openRemoveAccessDialog={removeAccessDialog.onOpenDialog}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
